import '@/styles/index.css';
import { afterFirstPaint, startPreloads } from '@/app/boot-gate';
import { canHydrate } from '@/app/hydratable';

const container = document.getElementById('root');
if (!container) throw new Error('#root not found');

/**
 * Fetch and start the app (`app/boot.tsx`): it hydrates the pre-rendered page (scripts/prerender.ts) when `#root`
 * holds one for this address and renders from scratch otherwise. The app is a dynamic import on purpose — this
 * entry chunk is only the gate below (a kilobyte), so the page's JavaScript is not part of the first paint.
 */
const hydrating = canHydrate(container, window.location);

/**
 * The service worker (lib/pwa): fetched and registered only once the app has started, the page has loaded and the
 * browser is idle, so installing it never competes with the first paint or the hydration. In development there is none.
 */
const scheduleServiceWorker = (): void => {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  const start = (): void => {
    void import('@/lib/pwa/service-worker').then(({ registerServiceWorker }) =>
      registerServiceWorker(),
    );
  };
  const idle = (): void => {
    // A few seconds after the page is up, then at an idle moment — not at the first idle moment: the registration
    // code (about 5 kB gzip) is not part of what a visitor downloads to get the page going (the byte budget of
    // apps/web/budgets.json counts what is requested until the app is up), and nothing needs the worker earlier.
    window.setTimeout(() => {
      // Safari has no requestIdleCallback
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(start, { timeout: 10_000 });
      } else start();
    }, 4000);
  };
  if (document.readyState === 'complete') idle();
  else window.addEventListener('load', idle, { once: true });
};

// Chromium announces "installable" early, usually before the app has started: keep the event for the polite,
// engagement-based invitation (lib/pwa/install.ts) and hold the browser's own banner back.
window.addEventListener('beforeinstallprompt', (event) => {
  event.preventDefault();
  Reflect.set(window, Symbol.for('sbg.install-prompt'), event);
});

const boot = (): void => {
  startPreloads();
  void import('@/app/boot')
    .then(({ bootClient }) => bootClient(container, hydrating))
    .then(scheduleServiceWorker);
};

// A pre-rendered page for this very address is on screen and complete: hydrate it after the first paint (see
// `afterFirstPaint`). Anything else — the empty shell served for the account area, sign-in and back-office, a
// page that must be rendered afresh (`/shop?color=black`), the 404 page the router may answer differently —
// has nothing to show that is worth waiting behind: start straight away.
if (hydrating) afterFirstPaint(boot);
else boot();
